import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Single source of truth for the beforeinstallprompt event.
 * Both InstallButton and InstallBanner consume this hook.
 */
export default function useInstallPrompt() {
  const deferredPrompt = useRef(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Platform detection (computed, not state)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isIOSSafari = isIOS && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS/.test(navigator.userAgent);

  // Capture beforeinstallprompt event
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault(); // Prevent browser's default mini-infobar
      deferredPrompt.current = e;
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Detect standalone mode
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(display-mode: standalone)');
    if (!mq) return; // JSDOM returns undefined
    setIsInstalled(mq.matches || navigator.standalone === true);
    const handler = (e) => setIsInstalled(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Listen for appinstalled
  useEffect(() => {
    const handler = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      deferredPrompt.current = null;
    };
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt.current) return null;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    setIsInstallable(false);
    return outcome; // 'accepted' or 'dismissed'
  }, []);

  return { isInstallable, isInstalled, isIOS, isIOSSafari, promptInstall };
}
